/**
 * Shared helpers for working with course schedules.
 *
 * Courses can use either:
 *   - Legacy single-time format: schedule.days[] + schedule.startTime + schedule.endTime
 *   - New per-day format: schedule.daySchedules[{day, startTime, endTime}]
 *
 * These helpers normalize both formats so callers can write logic once.
 */

// Resolve the join link for a course. Prefer a Zoom OAuth-created meeting
// (course.zoom.joinUrl), fall back to the manual link the studio pasted on
// the course form (course.location.meetLink). Provider-agnostic — the
// fallback link can be Zoom, Google Meet, Teams, Jitsi, anything.
export const getJoinLink = (course) =>
  course?.zoom?.joinUrl || course?.location?.meetLink || '';

export const TZ_SHORT = {
  'America/New_York': 'ET',
  'America/Chicago': 'CT',
  'America/Denver': 'MT',
  'America/Los_Angeles': 'PT',
  'Asia/Kolkata': 'IST'
};

export const DAY_ORDER = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];

/**
 * Returns a normalized array of {day, startTime, endTime} for a course,
 * regardless of which schedule format the course uses.
 */
export function getCourseScheduleEntries(course) {
  const sched = course && course.schedule;
  if (!sched) return [];

  // Prefer new per-day format
  if (Array.isArray(sched.daySchedules) && sched.daySchedules.length > 0) {
    return sched.daySchedules
      .filter(d => d && d.day)
      .map(d => ({
        day: d.day,
        startTime: d.startTime || '',
        endTime: d.endTime || ''
      }));
  }

  // Fallback: legacy single-time format
  if (Array.isArray(sched.days) && sched.days.length > 0) {
    return sched.days.map(day => ({
      day,
      startTime: sched.startTime || '',
      endTime: sched.endTime || ''
    }));
  }

  return [];
}

/**
 * Returns the schedule entry for a specific day name (case-insensitive), or null.
 */
export function getScheduleForDay(course, dayName) {
  if (!dayName) return null;
  const target = dayName.toLowerCase();
  return getCourseScheduleEntries(course).find(e => e.day === target) || null;
}

/**
 * Returns just the array of day names a course meets on (e.g., ['saturday', 'sunday']).
 */
export function getCourseDays(course) {
  return getCourseScheduleEntries(course).map(e => e.day);
}

export function capitalize(s, len) {
  if (!s) return '';
  const word = s.charAt(0).toUpperCase() + s.slice(1);
  return len ? word.slice(0, len) : word;
}

export function sortEntriesByDayOrder(entries) {
  return [...entries].sort((a, b) => DAY_ORDER.indexOf(a.day) - DAY_ORDER.indexOf(b.day));
}

/**
 * Format a course schedule for display.
 *
 * Options:
 *   short: true → abbreviate day names to 3 chars (e.g., "Sat" instead of "Saturday")
 *   includeEndTime: false → omit "– endTime" in time strings (default true)
 *   separator: separator between days (default ", ")
 *
 * Examples:
 *   All same time:    "Saturday, Sunday · 12:00 PM – 1:00 PM CT"
 *   Different times:  "Saturday 9:30 AM – 10:30 AM, Sunday 1:00 PM – 2:00 PM CT"
 *   Short:            "Sat, Sun · 12:00 PM CT"
 */
export function formatCourseSchedule(course, opts = {}) {
  const entries = sortEntriesByDayOrder(getCourseScheduleEntries(course));
  if (entries.length === 0) return '';

  const tz = course && course.schedule && course.schedule.timezone;
  const tzShort = TZ_SHORT[tz] || '';
  const includeEndTime = opts.includeEndTime !== false; // default true
  const separator = opts.separator || ', ';

  const formatTime = (entry) => {
    if (!entry.startTime) return '';
    if (includeEndTime && entry.endTime) {
      return `${entry.startTime} – ${entry.endTime}`;
    }
    return entry.startTime;
  };

  // Compact form when all entries share the same time
  const allSameTime = entries.every(e =>
    e.startTime === entries[0].startTime && e.endTime === entries[0].endTime
  );

  if (allSameTime) {
    const days = entries.map(e => capitalize(e.day, opts.short ? 3 : null)).join(separator);
    const time = formatTime(entries[0]);
    if (!time) return days;
    return `${days} · ${time}${tzShort ? ' ' + tzShort : ''}`;
  }

  // Per-day form
  const parts = entries.map(e => {
    const day = capitalize(e.day, opts.short ? 3 : null);
    const time = formatTime(e);
    return time ? `${day} ${time}` : day;
  });
  return `${parts.join(separator)}${tzShort ? ' ' + tzShort : ''}`;
}
